import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

const BACKDROP_STYLE = { zIndex: 1040 }

const PANEL_BASE_STYLE = {
  top: 0,
  right: 0,
  width: '100%',
  zIndex: 1050,
  display: 'flex',
  flexDirection: 'column'
}

const PANEL_CLASS = 'tw:fixed tw:h-full tw:bg-white tw:shadow-xl '
  + 'tw:transition-transform tw:duration-300 tw:ease-in-out '
  + 'tw:motion-reduce:transition-none'

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), '
  + 'select:not([disabled]), textarea:not([disabled]), '
  + '[tabindex]:not([tabindex="-1"])'

const noop = () => null

const Drawer = ({
  isOpen, onClose, ariaLabel, maxWidth, closeOnEscape, closeOnBackdrop, children
}) => {
  const panelRef = useRef(null)
  const triggerRef = useRef(null)

  useEffect(() => {
    if (!isOpen)
      return noop
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'
    return () => { root.style.overflow = previousOverflow }
  }, [isOpen])

  // Capture the trigger on open, move focus inside the panel, restore on close.
  useEffect(() => {
    if (!isOpen)
      return noop
    triggerRef.current = document.activeElement
    const focusable = panelRef.current?.querySelector(FOCUSABLE_SELECTOR)
    focusable?.focus({ preventScroll: true })
    return () => {
      const trigger = triggerRef.current
      if (trigger && typeof trigger.focus === 'function')
        trigger.focus({ preventScroll: true })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !closeOnEscape)
      return noop
    const handler = event => {
      if (event.key === 'Escape')
        onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, closeOnEscape, onClose])

  // Trap Tab/Shift+Tab inside the dialog so keyboard users stay in the modal.
  useEffect(() => {
    if (!isOpen)
      return noop
    const handler = event => {
      if (event.key !== 'Tab' || !panelRef.current)
        return
      const focusables = Array.from(
        panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      )
      if (focusables.length === 0)
        return
      const [first] = focusables
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen])

  const panelStyle = {
    ...PANEL_BASE_STYLE,
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    transform: `translateX(${isOpen ? '0' : '100%'})`
  }

  const content = (
    <>
      <div
        aria-hidden="true"
        className={`modal-backdrop fade ${isOpen ? 'show' : ''}`}
        style={{ ...BACKDROP_STYLE, display: isOpen ? 'block' : 'none' }}
        onClick={closeOnBackdrop ? onClose : noop}
      />
      <aside
        ref={panelRef}
        aria-hidden={!isOpen}
        aria-label={ariaLabel}
        aria-modal={isOpen ? 'true' : null}
        className={PANEL_CLASS}
        role="dialog"
        style={panelStyle}
      >
        {children}
      </aside>
    </>
  )

  return createPortal(content, document.body)
}

Drawer.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired
}

Drawer.defaultProps = {
  closeOnBackdrop: true,
  closeOnEscape: true,
  maxWidth: 620
}

export default Drawer
